import { Component, ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";
import { captureError } from "@/lib/observability";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * Error boundary for native app screens.
 * Catches rendering errors and shows a friendly recovery UI
 * instead of a white/blank screen crash.
 * 
 * IMPORTANT: Uses SPA navigation (history.pushState) instead of
 * window.location.assign to avoid full page reloads on native,
 * which would retrigger the startup splash screen.
 */
export class NativeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[NativeErrorBoundary] Caught error:", error.message, errorInfo.componentStack);

    captureError("native.error_boundary", error, {
      severity: "fatal",
      metadata: {
        componentStack: errorInfo.componentStack?.slice(0, 300),
        retryCount: this.state.retryCount,
      },
    });
    
    // Log to backend for diagnostics
    try {
      const payload = {
        message: error.message,
        stack: error.stack?.substring(0, 500),
        route: window.location.pathname,
        metadata: {
          componentStack: errorInfo.componentStack?.substring(0, 300),
          isNative: Capacitor.isNativePlatform(),
          retryCount: this.state.retryCount,
        },
      };
      navigator.sendBeacon?.(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-client-error`,
        JSON.stringify(payload)
      );
    } catch (_) {
      // Ignore beacon errors
    }
  }

  handleGoToDashboard = () => {
    // Use SPA-safe navigation to avoid full page reload on native
    // which would retrigger the startup splash screen
    console.log("[NativeErrorBoundary] Navigating to dashboard via SPA");
    this.setState({ hasError: false, error: null, retryCount: 0 });
    
    // Use history API for SPA navigation
    window.history.pushState({}, "", "/dashboard");
    window.dispatchEvent(new PopStateEvent("popstate"));
    
    // NEVER use window.location.href as fallback - it causes full page
    // reload on native which re-triggers the splash screen loop
  };

  handleRetry = () => {
    this.setState((prev) => ({ 
      hasError: false, 
      error: null, 
      retryCount: prev.retryCount + 1 
    }));
  };

  render() {
    if (this.state.hasError) {
      const maxRetriesReached = this.state.retryCount >= 3;
      
      return (
        <div role="alert" className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            {this.props.fallbackTitle || "Something went wrong"}
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {maxRetriesReached
              ? "This screen keeps encountering an error. Please go back to the dashboard."
              : "This screen encountered an error. You can try again or go back."}
          </p>
          <div className="flex gap-3">
            {!maxRetriesReached && (
              <Button variant="outline" onClick={this.handleRetry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            <Button onClick={this.handleGoToDashboard}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
